import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { ROLES } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Search, Plus, ArrowRight, ClipboardList } from 'lucide-react';
import { fmtDate } from '../lib/format';

const ClientsPage = () => {
  const { user, visibleClients, visibleSessions, USERS: allUsers } = useApp();
  const nav = useNavigate();
  const [q, setQ] = React.useState('');
  const [filter, setFilter] = React.useState('all');
  const [rmFilter, setRmFilter] = React.useState('all');

  const isRM = user?.role === ROLES.RM;
  const rms = allUsers.filter((u) => u.role === ROLES.RM && (user?.role === ROLES.ADMIN || (user?.role === ROLES.SALES_HEAD && user.rm_ids?.includes(u.user_id))));

  const filtered = visibleClients.filter((c) => {
    const s = q.trim().toLowerCase();
    const nameMatch = !s || c.name.toLowerCase().includes(s) || c.city.toLowerCase().includes(s) || c.phone.toLowerCase().includes(s);
    const kycMatch = filter === 'all' || c.kyc_status === filter;
    const rmMatch = rmFilter === 'all' || c.rm_id === rmFilter;
    return nameMatch && kycMatch && rmMatch;
  });

  const sessionCount = (cid) => visibleSessions.filter((s) => s.client_id === cid).length;
  const lastSession = (cid) => {
    const list = visibleSessions.filter((s) => s.client_id === cid).sort((a, b) => new Date(b.session_date) - new Date(a.session_date));
    return list[0]?.session_date;
  };
  const rmName = (rmId) => allUsers.find((u) => u.user_id === rmId)?.name || '—';

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1400px] mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Clients</div>
          <h1 className="text-3xl font-bold text-brand-indigo">{isRM ? 'My Clients' : 'All Clients'}</h1>
          <p className="text-sm text-muted-foreground mt-1">{visibleClients.length} client{visibleClients.length !== 1 && 's'} in your view.</p>
        </div>
        {isRM && (
          <Button onClick={() => nav('/clients/new')} className="bg-brand-indigo hover:bg-brand-indigoLight text-white">
            <Plus className="h-4 w-4 mr-2" /> New Client
          </Button>
        )}
      </div>

      <Card className="border-brand-border">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, phone or city" className="pl-9 h-10" />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {['all', 'verified', 'pending'].map((f) => (
                <Button key={f} variant={filter === f ? 'default' : 'outline'} className={filter === f ? 'bg-brand-indigo text-white' : 'text-brand-indigo border-brand-border'} onClick={() => setFilter(f)} size="sm">
                  {f === 'all' ? 'All KYC' : f === 'verified' ? 'KYC Verified' : 'KYC Pending'}
                </Button>
              ))}
              {!isRM && rms.length > 1 && (
                <>
                  <div className="h-6 w-px bg-brand-border mx-1" />
                  <Button variant={rmFilter === 'all' ? 'default' : 'outline'} className={rmFilter === 'all' ? 'bg-brand-indigo text-white' : 'text-brand-indigo border-brand-border'} onClick={() => setRmFilter('all')} size="sm">All RMs</Button>
                  {rms.map((rm) => (
                    <Button key={rm.user_id} variant={rmFilter === rm.user_id ? 'default' : 'outline'} className={rmFilter === rm.user_id ? 'bg-brand-indigo text-white' : 'text-brand-indigo border-brand-border'} onClick={() => setRmFilter(rm.user_id)} size="sm">{rm.name}</Button>
                  ))}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <Card key={c.id} className="border-brand-border hover:shadow-md transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-brand-indigo truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{c.city} · PAN ••••{c.pan_last4}</div>
                </div>
                <Badge variant="outline" className={c.kyc_status === 'verified' ? 'text-emerald-700 border-emerald-300 bg-emerald-50' : 'text-amber-700 border-amber-300 bg-amber-50'}>
                  {c.kyc_status === 'verified' ? 'Verified' : 'Pending'}
                </Badge>
              </div>
              {!isRM && (
                <div className="mt-2 text-[11px] text-muted-foreground">RM: <span className="font-semibold text-brand-indigo">{rmName(c.rm_id)}</span></div>
              )}
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-brand-bg rounded-md px-3 py-2">
                  <div className="text-muted-foreground">Sessions</div>
                  <div className="font-semibold text-brand-indigo flex items-center gap-1 mt-1"><ClipboardList className="h-3 w-3" /> {sessionCount(c.id)}</div>
                </div>
                <div className="bg-brand-bg rounded-md px-3 py-2">
                  <div className="text-muted-foreground">Last MRI</div>
                  <div className="font-semibold text-brand-indigo mt-1">{lastSession(c.id) ? fmtDate(lastSession(c.id)) : '—'}</div>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <Link to={`/clients/${c.id}`} className="text-xs text-brand-indigo font-semibold hover:underline flex items-center gap-1">Open profile <ArrowRight className="h-3 w-3" /></Link>
                {isRM && (
                  <Button size="sm" onClick={() => nav(`/clients/${c.id}/mri/new`)} className="bg-brand-green hover:bg-brand-greenDark text-white h-8">New MRI</Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-12 text-muted-foreground">No clients match your filters.</div>
        )}
      </div>
    </div>
  );
};

export default ClientsPage;
